import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Code, X, Users, BadgeDollarSign, Check } from "lucide-react";
import { TaskAssignee } from "@/types/kanban";
import { useToast } from "@/hooks/use-toast";
import { useGetEmployees } from "@/hooks/use-employees";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

interface CreateMFOCodeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: {
    code: string;
    title: string;
    budget: number;
    collaborators: TaskAssignee[];
  }) => void;
  mode: "create" | "update";
}

const CreateMFOCodeModal = ({ isOpen, onOpenChange, onSave }: CreateMFOCodeModalProps) => {
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>([]);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch real employee data
  const { data: employees = [], isLoading: isLoadingEmployees } = useGetEmployees();

  const handleReset = () => {
    setCode("");
    setTitle("");
    setBudget("");
    setSelectedCollaborators([]);
  };

  const handleSave = async () => {
    if (!code || !title || !budget) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Save MFO code to the database using any to bypass TypeScript checking
      const { data, error } = await supabase
        .from('mfo_codes' as any)
        .insert({
          code,
          title,
          budget: parseFloat(budget),
          owner_id: (await supabase.auth.getUser()).data.user?.id
        })
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      // Cast data to any to avoid TypeScript errors
      const dataAny = data as any;

      // Convert selected collaborator IDs to full objects
      const collaborators = employees
        .filter(emp => selectedCollaborators.includes(emp.id))
        .map(emp => ({
          id: emp.id,
          name: emp.name,
          avatar: emp.avatar
        }));

      // Call the onSave callback if provided
      if (onSave) {
        onSave({
          code,
          title,
          budget: parseFloat(budget),
          collaborators,
        });
      }

      toast({
        title: "MFO Code Created",
        description: `Successfully created MFO Code: ${code}`,
      });

      handleReset();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error creating MFO code:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create MFO code",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleCollaborator = (id: string) => {
    setSelectedCollaborators(prev => 
      prev.includes(id) 
        ? prev.filter(collabId => collabId !== id)
        : [...prev, id]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-primary" />
            Create MFO Code
          </DialogTitle>
          <DialogDescription>
            Create a new MFO Code as a container for related MFOs
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="mfo-code">MFO Code *</Label>
            <Input
              id="mfo-code"
              placeholder="e.g., MFO 1"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="mfo-title">MFO/PAP Title *</Label>
            <Input
              id="mfo-title"
              placeholder="Enter MFO title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="mfo-budget" className="flex items-center gap-1">
              <BadgeDollarSign className="h-4 w-4" /> Allocated Budget *
            </Label>
            <Input
              id="mfo-budget"
              placeholder="Enter budget amount"
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label className="flex items-center gap-1">
              <Users className="h-4 w-4" /> Collaborators
            </Label>
            <div className="border rounded-md p-2 max-h-[150px] overflow-y-auto">
              {isLoadingEmployees ? (
                <div className="space-y-2 p-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                </div>
              ) : employees.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">No employees available</p>
              ) : (
                employees.map((employee) => (
                  <div 
                    key={employee.id} 
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded-md"
                  >
                    <input 
                      type="checkbox"
                      id={`collab-${employee.id}`}
                      checked={selectedCollaborators.includes(employee.id)}
                      onChange={() => toggleCollaborator(employee.id)}
                      className="h-4 w-4"
                    />
                    <label 
                      htmlFor={`collab-${employee.id}`}
                      className="flex-grow cursor-pointer"
                    >
                      {employee.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Code className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Creating...' : 'Create MFO Code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateMFOCodeModal;
